import type {
  FraudAction,
  FraudAlertStatus,
  FraudAlertType,
  FraudOutcome,
  FraudSeverity,
} from "@/api/perkpoint/fraud";

/** Labels and colours for the fraud pages. Data comes from @/api/perkpoint/fraud. */

export type Severity = FraudSeverity;
export type AlertStatus = FraudAlertStatus;
export type AlertType = FraudAlertType;
export type RuleAction = FraudAction;

export const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  velocity: "Velocity",
  threshold: "Threshold",
  pattern: "Pattern",
  blockchain: "On-chain",
};

export const SEVERITY_LABELS: Record<Severity, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
};

export const STATUS_LABELS: Record<AlertStatus, string> = {
  active: "Open",
  investigating: "Investigating",
  resolved: "Resolved",
  false_positive: "False positive",
};

/** What happened to the transaction that raised the alert. */
export const OUTCOME_LABELS: Record<FraudOutcome, string> = {
  flagged: "Let through, flagged",
  rejected: "Transaction refused",
  member_blocked: "Refused, member blocked",
};

export const ACTION_OPTIONS: { value: RuleAction; label: string; hint: string }[] = [
  {
    value: "flag",
    label: "Flag for review",
    hint: "Raise an alert, let the transaction through",
  },
  {
    value: "block-earn-burn",
    label: "Block earn & burn",
    hint: "Refuse this transaction",
  },
  {
    value: "block-all-future",
    label: "Block member",
    hint: "Refuse it and block all future transactions",
  },
];

export const actionLabel = (action: RuleAction) =>
  ACTION_OPTIONS.find((option) => option.value === action)?.label ?? action;

export const SEVERITY_STYLES: Record<Severity, string> = {
  critical: "bg-red-50 text-red-700 ring-red-200",
  high: "bg-orange-50 text-orange-700 ring-orange-200",
  medium: "bg-amber-50 text-amber-700 ring-amber-200",
  low: "bg-sky-50 text-sky-700 ring-sky-200",
};

export const SEVERITY_DOT: Record<Severity, string> = {
  critical: "bg-red-500",
  high: "bg-orange-500",
  medium: "bg-amber-400",
  low: "bg-sky-400",
};

export const STATUS_STYLES: Record<AlertStatus, string> = {
  active: "bg-red-50 text-red-700",
  investigating: "bg-amber-50 text-amber-700",
  resolved: "bg-green-50 text-green-700",
  false_positive: "bg-slate-100 text-slate-600",
};

export const riskTone = (score: number) =>
  score >= 85 ? "bg-red-500" : score >= 70 ? "bg-orange-500" : score >= 50 ? "bg-amber-400" : "bg-sky-400";

/** Money in the program's currency, from major units. */
export function formatMoney(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `${amount.toLocaleString("en-US")} ${currency}`;
  }
}

/** Money in the program's currency, from minor units (what the API stores). */
export const formatMinor = (minor: number, currency: string, exponent = 2) =>
  formatMoney(minor / 10 ** exponent, currency);

export function formatDateTime(iso: string) {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

type RuleConditions = {
  velocity?: { transactions: number; days: number; action: RuleAction } | null;
  invoice?: { amount: number; action: RuleAction } | null;
  redemption?: { points: number; action: RuleAction } | null;
};

/**
 * Plain-language "if … then …" lines for a rule's conditions. The API returns
 * the same lines as `summary`; this builds them for the dialog's live preview.
 */
export function describeRule(rule: RuleConditions, currency: string): string[] {
  const lines: string[] = [];
  if (rule.velocity) {
    lines.push(
      `${rule.velocity.transactions}+ transactions in ${rule.velocity.days} day${
        rule.velocity.days === 1 ? "" : "s"
      } → ${actionLabel(rule.velocity.action)}`,
    );
  }
  if (rule.invoice) {
    lines.push(`Invoice over ${formatMoney(rule.invoice.amount, currency)} → ${actionLabel(rule.invoice.action)}`);
  }
  if (rule.redemption) {
    lines.push(
      `Redeem over ${rule.redemption.points.toLocaleString("en-US")} pts on one bill → ${actionLabel(
        rule.redemption.action,
      )}`,
    );
  }
  return lines;
}
